import {forwardRef} from 'react';
import {Link} from '@ezcater/tapas';
import {twMerge} from 'tailwind-merge';

import {compilePath} from '@/utils';
import useNavLinkTracking from './hooks/useNavlinkTracking';

type AppBarLinkProps = Omit<React.ComponentPropsWithRef<typeof Link>, 'variant'> & {
  href?: string;
  label: string;
  path?: string;
  queryParams?: Record<string, string>;
  trackingText?: string;
  trackingVersion?: string;
  datadogAction?: string;
  variant?: 'default' | 'contrast' | 'button' | 'contrastButton' | 'secondaryButton';
};

type Ref = HTMLAnchorElement;

const AppBarLink = forwardRef<Ref, AppBarLinkProps>(function AppBarLink(
  {
    href,
    label,
    path,
    queryParams,
    trackingText,
    trackingVersion,
    datadogAction,
    variant = 'default',
    onClick,
    ...props
  },
  ref,
) {
  const {trackLink} = useNavLinkTracking();
  const trackedPath = href || (path ? compilePath(path) : '');
  const linkHref = !href && path && queryParams ? compilePath(path, {}, queryParams) : trackedPath;
  const isButtonVariant =
    variant === 'button' || variant === 'contrastButton' || variant === 'secondaryButton';

  return (
    <Link
      href={linkHref}
      onClick={event => {
        // Compose rather than let `{...props}` override: a parent (e.g. MenuPopover's Base UI
        // trigger) may pass its own handler, and tracking must still fire.
        onClick?.(event);
        trackLink({
          path: trackedPath,
          title: trackingText ?? label,
          version: trackingVersion,
          datadogAction,
        });
      }}
      ref={ref}
      {...props}
      className={twMerge(
        'whitespace-nowrap text-peppercorn-800 no-underline hover:text-peppercorn-800 active:text-peppercorn-800',
        isButtonVariant &&
          'rounded-full px-4 py-3 font-bold hover:no-underline active:no-underline',
        (variant === 'default' || variant === 'contrast') && 'px-2 font-bold',
        variant === 'contrast' && 'text-white hover:text-white active:text-white',
        variant === 'button' &&
          'bg-green-700 text-white hover:bg-green-600 hover:text-white active:bg-green-800 active:text-white',
        variant === 'contrastButton' &&
          'border-none bg-transparent text-white hover:bg-white active:bg-peppercorn-200',
        variant === 'secondaryButton' &&
          'bg-white text-peppercorn-800 hover:bg-peppercorn-100 active:bg-peppercorn-200',
        props.className,
      )}
    >
      {label}
    </Link>
  );
});

export default AppBarLink;
